import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../services/authService";

// Thunks
export const login = createAsyncThunk("auth/login", async (credentials) => {
  const response = await authService.login(credentials);
  return response;
});

export const register = createAsyncThunk("auth/register", async (userData) => {
  const response = await authService.register(userData);
  return response;
});

export const updateUserProfile = createAsyncThunk(
  "auth/updateProfile",
  async (updates) => {
    const response = await authService.updateProfile(updates);
    return response;
  }
);

export const deleteAccount = createAsyncThunk(
  "auth/deleteAccount",
  async () => {
    const response = await authService.deleteAccount();
    return response;
  }
);

export const hydrateSession = createAsyncThunk("auth/hydrateSession", async () => {
  if (!authService.isAuthenticated()) {
    return null;
  }

  const user = await authService.validateToken();
  return {
    token: authService.getToken(),
    user,
  };
});

const initialState = {
  user: null,
  token: localStorage.getItem("token"),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
      localStorage.setItem("token", action.payload.token);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("token");
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem("token", action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete Account
      .addCase(deleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem("token");
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Hydrate session
      .addCase(hydrateSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(hydrateSession.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) {
          state.user = null;
          state.token = null;
          return;
        }
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(hydrateSession.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        localStorage.removeItem("token");
      });
  },
});

export const { setAuth, logout, clearError } = authSlice.actions;
export default authSlice.reducer;
